import { Vector2 } from "three";
import { HSLColor } from "../../../colors/HSLColor";
import { HTMLColor } from "../../../colors/HTMLColor";
import { CanvasDrawer } from "../../../dom/CanvasDrawer";
import { Property } from "../../../events/Property";
import { MathX } from "../../../math/MathX";
import { Arrays } from "../../../tools/Arrays";
import { WaveForm } from "./WaveForm";

export class WaveFormRenderer
{
  _canvas:HTMLCanvasElement;
  _drawer:CanvasDrawer;

  _waveform:WaveForm;
  _waveform2:WaveForm;
  
  readonly normalizedStart:Property<number> = new Property( 0 );
  readonly normalizedEnd:Property<number> = new Property( 1 );



  readonly backgroundColor:Property<HTMLColor> = new Property<HTMLColor>( new HSLColor( 0, 0, 10, 1 ) );
  readonly waveColor:Property<HTMLColor> = new Property<HTMLColor>( new HSLColor( 220, 60, 80 ) );

  readonly samplingLevelOffset:Property<number> = new Property<number>( 0 );

  _data:Vector2[] = [];

  constructor( canvas:HTMLCanvasElement, waveform:WaveForm, waveform2:WaveForm = null )
  {
    this._canvas = canvas;
    this._drawer = new CanvasDrawer( canvas );
    this._waveform = waveform;
    this._waveform2 = waveform2;
  }

  getDataPositionAtNormalizedX( x:number )
  {
    return MathX.lerp( this.normalizedStart.value, this.normalizedEnd.value, x );
  }

  changeSamplingLevel( movement:Vector2, normalizedCenter:number, viewSize:Vector2, relativeScaleSpeed:number = 2 )
  {
    let value = this.samplingLevelOffset.value;

    let newValue = MathX.clamp( value - movement.y / 400, -1, 10 ); 
    this.samplingLevelOffset.value = newValue;

    console.log( this.samplingLevelOffset.value, newValue, movement.y);

    this.renderWave();
  }

  changeView( movement:Vector2, normalizedCenter:number, viewSize:Vector2, relativeScaleSpeed:number = 2 )
  {
    let scale = Math.pow( relativeScaleSpeed, movement.y / viewSize.y );
    let offset = -movement.x / viewSize.x * ( this.normalizedEnd.value - this.normalizedStart.value );

    let start = this._changeDimension( this.normalizedStart.value, normalizedCenter, scale, offset );
    let end   = this._changeDimension( this.normalizedEnd.value, normalizedCenter, scale, offset );

    this.normalizedStart.value = start;
    this.normalizedEnd.value = end;


    this.renderWave();
  } 

  _changeDimension( value:number, scaleCenter:number, scale:number, offset:number )
  {
    value -= scaleCenter;
    value *= scale;
    value += scaleCenter;

    value += offset;

    return value;
  }

  _ensureDataLength()
  {
    if ( this._data.length == this._canvas.width )
    {
      return;
    }
    
    this._data = Arrays.create( this._canvas.width, i => new Vector2( 0, 0 ) );
    

  }

  renderWave()
  { 
    this._ensureDataLength();

    let start = this.normalizedStart.value;
    let end   = this.normalizedEnd.value;

    this._drawer.fillBackground( this.backgroundColor.value );



    this._waveform.sampleDataNormalized( start, end, this._data, -this._drawer.height / 2 * 0.9, this.samplingLevelOffset.value );

    this._ensureMinMaxInXY();


    let edgeColor   = this.backgroundColor.value.clone();
    let centerColor = this.waveColor.value.clone();

    this._drawer.drawGradientWaveShape( edgeColor, centerColor, 0.4, this._data, new Vector2( 0.5, this._drawer.height / 2 ) );
    
    this._drawer.setLineStyle( this.waveColor.value, 1 ); 
    this._drawer.drawWaveShapeCenterStroke( this._data, new Vector2( 0.5, this._drawer.height / 2 ) );


    let samplesPerEntry = this._waveform.getSamplesPerEntryNormalized( start, end, this._data );

    if ( samplesPerEntry < 0.4 )
    {
      let positions = this._waveform.getPoints( start, end );  
      
      positions.forEach( 
        p => 
        {
          p.x = MathX.map( p.x, start, end, 0, this._drawer.width );
          p.y = p.y * -this._drawer.height / 2 * 0.9;
        }
      ); 

      this._drawer.setFillStyle( this.waveColor.value );

      let sizeModifier = MathX.mapClamped( samplesPerEntry, 0.4, 0.01, 0, 1 ); 

      sizeModifier = Math.pow( sizeModifier, 5 );
      
      console.log( "Drawing points for", samplesPerEntry, "num:", positions.length, "size:", sizeModifier );
      this._drawer.drawCircles( positions, 10 * sizeModifier, new Vector2( 0.5, this._drawer.height / 2 ) );
    
    }



  }

  _ensureMinMaxInXY()
  {
    this._data.forEach(
      ( d )=>
      {
        if ( d.x > d.y )
        {
          let x = d.x;
          d.x = d.y;
          d.y = x;
        }
      }
    );
  }
}